// Shared field-level building blocks for the data-entry sheets (Measurement,
// Milestone, …). These existed three times before; consolidated here so the
// "optional time" pattern, the description textarea and the delete-confirm
// affordance evolve in one place.

import { useState, type ReactNode } from 'react';
import { Icon } from '../Icon.tsx';
import { FieldRow } from './SheetShell.tsx';
import { TimePicker } from './TimePicker.tsx';

/** Noon-today in local time, expressed as Unix seconds. Used as a sensible
 *  "no specific time" default when an entry has only a date. */
export function noonTodayLocal(): number {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

/** When loading an existing entry, decide whether the user originally typed a
 *  time or accepted the noon/midnight default. Sentinel times collapse the
 *  picker; explicit times reveal it. */
export function timeIsSentinel(sec: number): boolean {
  const d = new Date(sec * 1000);
  return (
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    (d.getHours() === 12 || d.getHours() === 0)
  );
}

type DateWithOptionalTimeProps = {
  occurredAt: number;
  onChange: (sec: number) => void;
  showTime: boolean;
  onToggleTime: (next: boolean) => void;
};

/** Date input plus reveal-on-demand TimePicker. Snaps to noon on the new
 *  date when time isn't explicitly set. */
export function DateWithOptionalTime({
  occurredAt,
  onChange,
  showTime,
  onToggleTime,
}: DateWithOptionalTimeProps) {
  const dateOverride = new Date(occurredAt * 1000).toISOString().slice(0, 10);

  function applyDateOverride(yyyy_mm_dd: string) {
    const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
    if (!y || !m || !d) return;
    const cur = new Date(occurredAt * 1000);
    cur.setFullYear(y, m - 1, d);
    if (!showTime) cur.setHours(12, 0, 0, 0);
    onChange(Math.floor(cur.getTime() / 1000));
  }

  return (
    <>
      <FieldRow
        label="Date"
        value={
          <input
            type="date"
            value={dateOverride}
            onChange={(e) => applyDateOverride(e.target.value)}
            className="lt-mono lt-tabular"
            style={{ fontSize: 16, color: 'var(--text-1)' }}
          />
        }
        right={
          <button
            type="button"
            className="lt-btn lt-btn-ghost"
            style={{ padding: '6px 10px', fontSize: 12 }}
            onClick={() => onToggleTime(!showTime)}
          >
            {showTime ? 'Hide time' : 'Add time'}
          </button>
        }
      />
      {showTime && <TimePicker label="Time" value={occurredAt} onChange={onChange} />}
    </>
  );
}

type DescriptionFieldProps = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label?: string;
};

export function DescriptionField({
  value,
  onChange,
  placeholder = 'optional',
  label = 'Description',
}: DescriptionFieldProps) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          marginBottom: 6,
          paddingLeft: 4,
        }}
      >
        {label}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 14,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          color: 'var(--text-1)',
          fontSize: 14,
          resize: 'none',
        }}
      />
    </div>
  );
}

type ConfirmDeleteProps = {
  /** Sentence rendered next to the trash icon. */
  message?: ReactNode;
};

export function ConfirmDeletePanel({
  message = 'This is permanent. The entry will disappear from the chart, stats, and feed.',
}: ConfirmDeleteProps) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 12,
        background: 'color-mix(in oklab, var(--danger) 12%, transparent)',
        border: '1px solid color-mix(in oklab, var(--danger) 30%, transparent)',
        fontSize: 13,
        color: 'var(--text-1)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <Icon.trash s={16} style={{ color: 'var(--danger)', marginTop: 2 }} />
      <div>{message}</div>
    </div>
  );
}

/** Two-step delete state for sheet primary buttons. First click flips
 *  `confirming` true; the parent's primary button label/style change to
 *  "Yes, delete" + danger styling; the second click runs `onDelete`. */
export function useConfirmDelete(): {
  confirming: boolean;
  startConfirm: () => void;
  reset: () => void;
} {
  const [confirming, setConfirming] = useState(false);
  return {
    confirming,
    startConfirm: () => setConfirming(true),
    reset: () => setConfirming(false),
  };
}
